/**
 * COUNTERTOPS (spec §63).
 *
 * A countertop was already a derived rectangle following a cabinet run, and
 * an assembly priced per square foot. What it could not say was what it is
 * MADE of, how far it hangs over, what has to be cut out of it, or whether
 * the number came from a calculation or from a fabricator's quote — which
 * are the four things anybody actually asks about a countertop.
 *
 * The numbers here are illustrative assumptions for a small flip, in the
 * same sense as every other seeded cost in Berrier Reno: they make the
 * estimate real enough to be useful and are labelled so that nobody mistakes
 * them for a quote.
 *
 * Pure domain: no SQL, no React, no geometry engine.
 */
import { PU_PER_FOOT, PU_PER_INCH } from "../geometry/units";

export type CountertopMaterialId =
  | "laminate"
  | "butcher-block"
  | "granite"
  | "quartz"
  | "marble"
  | "solid-surface"
  | "concrete"
  | "tile"
  | "custom";

export interface CountertopMaterial {
  id: CountertopMaterialId;
  label: string;
  /** Illustrative installed material cost per square foot. */
  costPerSqftCents: number;
  /** What a slab of it is usually cut at, in inches × 10. */
  thicknessInX10: number;
  /**
   * True when a retailer product search is meaningful. Slab stone is bought
   * through a fabricator, and sending somebody to a search page for it
   * would produce misleading results (spec §63G).
   */
  retailSearchable: boolean;
  note: string;
}

export const COUNTERTOP_MATERIALS: readonly CountertopMaterial[] = [
  { id: "laminate", label: "Laminate", costPerSqftCents: 2800, thicknessInX10: 15, retailSearchable: true, note: "Sold by the linear foot in stock sizes." },
  { id: "butcher-block", label: "Butcher block", costPerSqftCents: 4500, thicknessInX10: 15, retailSearchable: true, note: "Sold in stock slabs; cut on site." },
  { id: "tile", label: "Tile", costPerSqftCents: 3200, thicknessInX10: 10, retailSearchable: true, note: "Set on site over a substrate." },
  { id: "solid-surface", label: "Solid surface", costPerSqftCents: 7500, thicknessInX10: 12, retailSearchable: false, note: "Fabricated to template." },
  { id: "granite", label: "Granite", costPerSqftCents: 6500, thicknessInX10: 30, retailSearchable: false, note: "Slab, templated and fabricated." },
  { id: "quartz", label: "Quartz", costPerSqftCents: 8000, thicknessInX10: 30, retailSearchable: false, note: "Engineered slab, templated and fabricated." },
  { id: "marble", label: "Marble", costPerSqftCents: 11000, thicknessInX10: 30, retailSearchable: false, note: "Slab, templated and fabricated." },
  { id: "concrete", label: "Concrete", costPerSqftCents: 9000, thicknessInX10: 15, retailSearchable: false, note: "Cast to template." },
  { id: "custom", label: "Custom / other", costPerSqftCents: 0, thicknessInX10: 15, retailSearchable: false, note: "Priced by hand or from a quote." },
];

export function materialById(id: string | null | undefined): CountertopMaterial {
  return COUNTERTOP_MATERIALS.find((m) => m.id === id) ?? COUNTERTOP_MATERIALS[COUNTERTOP_MATERIALS.length - 1];
}

/** Where this piece of countertop sits. Islands and peninsulas overhang differently. */
export type CountertopLayout = "run" | "island" | "peninsula";

/**
 * The splash that comes WITH the countertop, and the one that does not.
 * A full-height splash is wall tile: Berrier Reno already has an Install
 * Backsplash feature for it, and pricing it twice would be a lie in the
 * user's favour (spec §63E).
 */
export type CountertopBacksplash = "none" | "standard" | "full-height";

export type CountertopEdge = "standard" | "upgraded";

export interface CountertopSpec {
  material: CountertopMaterialId;
  materialNote: string;
  /** Inches × 10; null = the material's usual thickness. */
  thicknessInX10: number | null;
  /** How far it hangs past the cabinet front, inches × 10. */
  overhangInX10: number;
  /** The seating side of an island, inches × 10; null = the same as the front. */
  seatingOverhangInX10: number | null;
  layout: CountertopLayout;
  backsplash: CountertopBacksplash;
  edge: CountertopEdge;
  /** Finished ends that fall to the floor. */
  waterfallEnds: number;
  sinkCutouts: number;
  cooktopCutouts: number;
}

export const DEFAULT_COUNTERTOP: CountertopSpec = {
  material: "quartz",
  materialNote: "",
  thicknessInX10: null,
  overhangInX10: 15,
  seatingOverhangInX10: null,
  layout: "run",
  backsplash: "standard",
  edge: "standard",
  waterfallEnds: 0,
  sinkCutouts: 1,
  cooktopCutouts: 0,
};

export const COUNTERTOP_HEIGHT_IN = 36;

/* ------------------------------------------------------------------ *
 * Figures
 * ------------------------------------------------------------------ */

export interface CountertopFigures {
  /** The top itself. */
  areaSqftX100: number;
  /** Material that goes down the ends, when there are waterfalls. */
  waterfallAreaSqftX100: number;
  /** Everything to be bought, cut and paid for. */
  totalAreaSqftX100: number;
  /** The exposed front edge, which is what an edge upgrade is priced by. */
  frontEdgeLfX100: number;
  /** Where a standard splash goes: the back of the run. */
  backsplashLfX100: number;
  cutouts: number;
  thicknessInX10: number;
}

const sqftX100 = (widthPu: number, depthPu: number): number =>
  Math.round((widthPu * depthPu * 100) / (PU_PER_FOOT * PU_PER_FOOT));

const lfX100 = (pu: number): number => Math.round((pu * 100) / PU_PER_FOOT);

/**
 * What the countertop actually amounts to. `lengthPu` and `depthPu` are the
 * finished top, overhang INCLUDED — the geometry engine has already added
 * it, so it is never added twice (spec §63C).
 */
export function countertopFigures(input: {
  lengthPu: number;
  depthPu: number;
  spec: CountertopSpec;
  /** Finished height off the floor; waterfall material falls this far. */
  heightInches?: number;
}): CountertopFigures {
  const spec = input.spec;
  const material = materialById(spec.material);
  const thicknessInX10 = spec.thicknessInX10 ?? material.thicknessInX10;
  const area = sqftX100(Math.max(0, input.lengthPu), Math.max(0, input.depthPu));
  const height = input.heightInches ?? COUNTERTOP_HEIGHT_IN;
  const waterfall =
    spec.waterfallEnds > 0
      ? sqftX100(Math.round(height * PU_PER_INCH), Math.max(0, input.depthPu)) * spec.waterfallEnds
      : 0;

  // An island is walked around: its whole perimeter is a finished edge. A
  // run is fixed to a wall, so only the front and the two ends show.
  const front = lfX100(input.lengthPu);
  const ends = lfX100(input.depthPu * 2);
  const frontEdge = spec.layout === "island" ? front * 2 + ends : front + ends;

  return {
    areaSqftX100: area,
    waterfallAreaSqftX100: waterfall,
    totalAreaSqftX100: area + waterfall,
    frontEdgeLfX100: frontEdge,
    // A splash only makes sense where the top meets a wall.
    backsplashLfX100: spec.layout === "island" ? 0 : front,
    cutouts: Math.max(0, spec.sinkCutouts) + Math.max(0, spec.cooktopCutouts),
    thicknessInX10,
  };
}

/** How much the top hangs over on each side, for the geometry engine. */
export function overhangPu(spec: CountertopSpec, side: "front" | "seating"): number {
  const tenths = side === "seating" ? spec.seatingOverhangInX10 ?? spec.overhangInX10 : spec.overhangInX10;
  return Math.round((tenths / 10) * PU_PER_INCH);
}

export function describeCountertop(spec: CountertopSpec, figures: CountertopFigures): string {
  const material = materialById(spec.material);
  const parts = [material.label, `${(figures.areaSqftX100 / 100).toFixed(1)} sqft`];
  if (spec.layout !== "run") parts.push(spec.layout);
  if (spec.waterfallEnds > 0) parts.push(`${spec.waterfallEnds} waterfall end${spec.waterfallEnds === 1 ? "" : "s"}`);
  if (figures.cutouts > 0) parts.push(`${figures.cutouts} cutout${figures.cutouts === 1 ? "" : "s"}`);
  if (spec.backsplash === "standard") parts.push("with a standard splash");
  if (spec.backsplash === "full-height") parts.push("full-height splash priced as wall tile");
  return parts.join(" · ");
}

/** What to ask a retailer for, when asking one makes sense (spec §63G). */
export function countertopSearchAttributes(spec: CountertopSpec, figures: CountertopFigures): Array<{ key: string; value: string }> {
  const material = materialById(spec.material);
  if (!material.retailSearchable) return [];
  const feet = Math.round(figures.frontEdgeLfX100 / 100);
  const attributes = [{ key: "type", value: `${material.label.toLowerCase()} countertop` }];
  if (feet > 0 && feet < 30) attributes.push({ key: "length", value: `${feet} ft` });
  return attributes;
}

export function validateCountertop(spec: CountertopSpec): string[] {
  const problems: string[] = [];
  if (spec.overhangInX10 < 0 || spec.overhangInX10 > 240) problems.push("That overhang is not a real overhang.");
  if (spec.seatingOverhangInX10 !== null && (spec.seatingOverhangInX10 < 0 || spec.seatingOverhangInX10 > 360)) {
    problems.push("That seating overhang is not a real overhang.");
  }
  if (spec.waterfallEnds < 0 || spec.waterfallEnds > 4) problems.push("A countertop has at most four ends.");
  if (spec.sinkCutouts < 0 || spec.cooktopCutouts < 0) problems.push("A cutout count cannot be negative.");
  if (spec.sinkCutouts > 6 || spec.cooktopCutouts > 6) problems.push("That is more cutouts than a countertop has room for.");
  return problems;
}
